import { lerp, baseline, baselineInvHours, type Anchor } from "./interpolate";
import type { YeastType } from "../types";

const ROOM_FACTORS: Record<number, number> = {
  14: 0.33, 15: 0.3, 16: 0.245, 17: 0.205, 18: 0.17, 19: 0.14, 20: 0.113,
  21: 0.1, 22: 0.08, 23: 0.0683, 24: 0.0538, 25: 0.0472, 26: 0.0389,
  27: 0.0309, 28: 0.0264, 29: 0.0218, 30: 0.019,
};
const COLD_FACTORS: Record<number, { r: number; k: number }> = {
  4: { r: 0.044, k: -1.19 }, 5: { r: 0.043, k: -1.21 }, 6: { r: 0.042, k: -1.22 },
  7: { r: 0.041, k: -1.23 }, 8: { r: 0.04, k: -1.24 }, 9: { r: 0.039, k: -1.25 },
  10: { r: 0.038, k: -1.26 }, 11: { r: 0.037, k: -1.27 }, 12: { r: 0.036, k: -1.28 },
  13: { r: 0.035, k: -1.29 },
};

function roomIDY(hours: number, temp: number): number {
  const T = Math.min(30, Math.max(14, temp));
  const lo = Math.floor(T), hi = Math.ceil(T);
  const factor = lo === hi ? ROOM_FACTORS[lo]!
                           : lerp(ROOM_FACTORS[lo]!, ROOM_FACTORS[hi]!, T - lo);
  return factor * Math.pow(hours + 2.75, -1.45) * 1.26 / 2.5 * 100;
}
function coldIDY(hours: number, temp: number): number {
  const T = Math.min(13, Math.max(4, temp));
  const lo = Math.floor(T), hi = Math.ceil(T);
  const r = lo === hi ? COLD_FACTORS[lo]!.r : lerp(COLD_FACTORS[lo]!.r, COLD_FACTORS[hi]!.r, T - lo);
  const k = lo === hi ? COLD_FACTORS[lo]!.k : lerp(COLD_FACTORS[lo]!.k, COLD_FACTORS[hi]!.k, T - lo);
  return r * (Math.pow(hours, k) / (Math.pow(9.8233, k) + Math.pow(hours, k))) / 2.5 * 100;
}

/* IDY % of flour for a single-stage ferment, blending the cold/room seam. */
export function idyPct(hours: number, temp: number): number {
  if (temp >= 14) return roomIDY(hours, temp);
  if (temp <= 13) return coldIDY(hours, temp);
  const f = temp - 13;
  return coldIDY(hours, 13) * (1 - f) + roomIDY(hours, 14) * f;
}

export const SD_ANCHORS: Anchor[] = [[8, 20], [12, 15], [24, 10], [48, 5]];

/* Q10-style temperature correction relative to 20C reference. */
export function tempFactor(temp: number): number {
  return Math.pow(1.5, (20 - temp) / 3);
}

/* Commercial yeast auto % (of flour). */
export function autoYeastPct(type: YeastType, hours: number, temp: number): number {
  const idy = idyPct(hours, temp);
  if (type === "ady") return idy * 1.25;
  if (type === "fresh") return idy * 3;
  return idy;
}

export const MAX_STARTER = 25;      // auto-calc cap: past this, extend time instead
export const HARD_MAX_STARTER = 50; // manual ceiling

export interface StarterResult { pct: number; capped: boolean; reqHours?: number; }
export function autoStarter(hours: number, temp: number): StarterResult {
  const tf = tempFactor(temp);
  const raw = baseline(SD_ANCHORS, hours) * tf;
  if (raw <= MAX_STARTER) return { pct: raw, capped: false };
  const reqHours = baselineInvHours(SD_ANCHORS, MAX_STARTER / tf);
  return { pct: MAX_STARTER, capped: true, reqHours };
}
